import {
  STORY_FOREST_MAP_BOUNDS,
  STORY_FOREST_WORLD,
  resolveStoryForestWalkablePoint,
} from "../../../@shared/utils/storyForestEncounters";
import { getVoidCityExteriorDoorSpawnByBuilding } from "../../../@shared/utils/voidCityDoorSpawns";
import caveSource from "../voidCave/void-cave-source.json";
import caveDepthsSource from "../voidCave/void-cave-depths-source.json";
import { VOID_CAVE_ENTRY_SPAWN } from "../voidCave/voidCaveMapConfig";
import { VOID_CAVE_DEPTHS_ENTRY_SPAWN } from "../voidCave/voidCaveDepthsMapConfig";
import {
  DEEP_GALLERY_MAP_CONFIG,
  RUNE_HOLLOW_MAP_CONFIG,
  WEST_GROTTO_MAP_CONFIG,
  WEST_PASSAGE_MAP_CONFIG,
} from "../voidCave/westPassageMapConfigs";
import {
  CRYSTAL_BASIN_MAP_CONFIG,
  EAST_PASSAGE_MAP_CONFIG,
  ECHO_VAULT_MAP_CONFIG,
  SHATTERED_REACH_MAP_CONFIG,
} from "../voidCave/eastPassageMapConfigs";

export const STORY_VOID_CITY_EXTERIOR_SCENE_KEY = "VoidCity2Scene";
export const STORY_WHISPERING_FOREST_SCENE_KEY = "WhisperingForestScene";
export const STORY_VOID_CAVE_SCENE_KEY = "VoidCaveScene";
export const STORY_VOID_CAVE_DEPTHS_SCENE_KEY = "VoidCaveDepthsScene";
export const STORY_GAME_HALL_SCENE_KEY = "GameHallScene";

export const STORY_WHISPERING_FOREST_ENTRY_SPAWN = Object.freeze({
  x: STORY_FOREST_WORLD.x,
  y: STORY_FOREST_WORLD.y,
});
export const STORY_QUEST_HALL_GLYDE_RETURN_SPAWN_ID = "questhall-glyde-return";
export const STORY_QUEST_HALL_ENTRY_SPAWN = Object.freeze({
  x: 323,
  y: 414,
});
export const STORY_VOID_CAVE_ENTRY_SPAWN = VOID_CAVE_ENTRY_SPAWN;
export const STORY_VOID_CAVE_DEPTHS_ENTRY_SPAWN = VOID_CAVE_DEPTHS_ENTRY_SPAWN;
export const STORY_GAME_HALL_ENTRY_SPAWN = Object.freeze({
  x: 768,
  y: 884,
});
export const STORY_VOID_CITY_QUEST_HALL_PLAZA_WORLD = Object.freeze({
  mapId: "voidCity",
  building: "",
  spawnId: "questhall-plaza",
  ...getVoidCityExteriorDoorSpawnByBuilding("questHall"),
});

const STORY_QUEST_HALL_BOUNDS = Object.freeze({
  minX: 48,
  maxX: 640,
  minY: 48,
  maxY: 480,
});

const STORY_WHISPERING_FOREST_BOUNDS = STORY_FOREST_MAP_BOUNDS;
const STORY_VOID_CAVE_BOUNDS = Object.freeze({
  minX: 48,
  maxX: Math.max(48, caveSource.width * caveSource.tilewidth - 48),
  minY: 48,
  maxY: Math.max(48, caveSource.height * caveSource.tileheight - 48),
});
const STORY_VOID_CAVE_DEPTHS_BOUNDS = Object.freeze({
  minX: 48,
  maxX: Math.max(48, caveDepthsSource.width * caveDepthsSource.tilewidth - 48),
  minY: 48,
  maxY: Math.max(48, caveDepthsSource.height * caveDepthsSource.tileheight - 48),
});

const buildCavernStoryRoute = (sceneKey, mapConfig) =>
  Object.freeze({
    sceneKey,
    entrySpawn: mapConfig.entrySpawn,
    bounds: Object.freeze({
      minX: 48,
      maxX: Math.max(
        48,
        mapConfig.source.width * mapConfig.source.tilewidth - 48,
      ),
      minY: 48,
      maxY: Math.max(
        48,
        mapConfig.source.height * mapConfig.source.tileheight - 48,
      ),
    }),
  });

// Generated cavern systems, keyed by normalized story map id.
const CAVERN_STORY_ROUTES = Object.freeze({
  westpassage: buildCavernStoryRoute("WestPassageScene", WEST_PASSAGE_MAP_CONFIG),
  westgrotto: buildCavernStoryRoute("WestGrottoScene", WEST_GROTTO_MAP_CONFIG),
  runehollow: buildCavernStoryRoute("RuneHollowScene", RUNE_HOLLOW_MAP_CONFIG),
  deepgallery: buildCavernStoryRoute("DeepGalleryScene", DEEP_GALLERY_MAP_CONFIG),
  eastpassage: buildCavernStoryRoute("EastPassageScene", EAST_PASSAGE_MAP_CONFIG),
  crystalbasin: buildCavernStoryRoute(
    "CrystalBasinScene",
    CRYSTAL_BASIN_MAP_CONFIG,
  ),
  echovault: buildCavernStoryRoute("EchoVaultScene", ECHO_VAULT_MAP_CONFIG),
  shatteredreach: buildCavernStoryRoute(
    "ShatteredReachScene",
    SHATTERED_REACH_MAP_CONFIG,
  ),
});
const STORY_GAME_HALL_BOUNDS = Object.freeze({
  minX: 48,
  maxX: 1488,
  minY: 48,
  maxY: 976,
});

const normalizeStoryMapId = (mapId = "") =>
  String(mapId || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const isFiniteCoordinate = (value) => Number.isFinite(Number(value));

const isInsideBounds = ({ x, y }, bounds) =>
  isFiniteCoordinate(x) &&
  isFiniteCoordinate(y) &&
  Number(x) >= bounds.minX &&
  Number(x) <= bounds.maxX &&
  Number(y) >= bounds.minY &&
  Number(y) <= bounds.maxY;

export const resolveStoryInitialSceneKey = (storyWorld = {}) => {
  const mapId = normalizeStoryMapId(storyWorld?.mapId);
  const building = String(storyWorld?.building || "").trim().toLowerCase();

  if (mapId === "whisperingforest") {
    return STORY_WHISPERING_FOREST_SCENE_KEY;
  }

  if (mapId === "voidcavedepths") {
    return STORY_VOID_CAVE_DEPTHS_SCENE_KEY;
  }

  if (CAVERN_STORY_ROUTES[mapId]) {
    return CAVERN_STORY_ROUTES[mapId].sceneKey;
  }

  if (mapId === "voidcave") {
    return STORY_VOID_CAVE_SCENE_KEY;
  }

  if (building === "arena") {
    return "ArenaScene";
  }

  if (building === "gamehall") {
    return STORY_GAME_HALL_SCENE_KEY;
  }

  if (
    building === "questhall" &&
    storyWorld?.spawnId === STORY_QUEST_HALL_GLYDE_RETURN_SPAWN_ID
  ) {
    return "QuestHallScene";
  }

  return STORY_VOID_CITY_EXTERIOR_SCENE_KEY;
};

export const resolveStoryInitialSceneSpawn = (storyWorld = {}) => {
  const mapId = normalizeStoryMapId(storyWorld?.mapId);
  const requestedSpawn = {
    x: storyWorld?.x,
    y: storyWorld?.y,
  };

  if (mapId === "whisperingforest") {
    if (isInsideBounds(requestedSpawn, STORY_WHISPERING_FOREST_BOUNDS)) {
      return resolveStoryForestWalkablePoint(
        {
          x: Number(requestedSpawn.x),
          y: Number(requestedSpawn.y),
        },
        STORY_WHISPERING_FOREST_ENTRY_SPAWN,
      );
    }

    return STORY_WHISPERING_FOREST_ENTRY_SPAWN;
  }

  if (CAVERN_STORY_ROUTES[mapId]) {
    const route = CAVERN_STORY_ROUTES[mapId];
    if (isInsideBounds(requestedSpawn, route.bounds)) {
      return {
        x: Number(requestedSpawn.x),
        y: Number(requestedSpawn.y),
        facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
          ? Number(storyWorld.facingDirection)
          : null,
      };
    }

    return {
      ...route.entrySpawn,
      facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
        ? Number(storyWorld.facingDirection)
        : null,
    };
  }

  if (mapId === "voidcavedepths") {
    if (isInsideBounds(requestedSpawn, STORY_VOID_CAVE_DEPTHS_BOUNDS)) {
      return {
        x: Number(requestedSpawn.x),
        y: Number(requestedSpawn.y),
        facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
          ? Number(storyWorld.facingDirection)
          : null,
      };
    }

    return {
      ...STORY_VOID_CAVE_DEPTHS_ENTRY_SPAWN,
      facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
        ? Number(storyWorld.facingDirection)
        : null,
    };
  }

  if (mapId === "voidcave") {
    if (isInsideBounds(requestedSpawn, STORY_VOID_CAVE_BOUNDS)) {
      return {
        x: Number(requestedSpawn.x),
        y: Number(requestedSpawn.y),
        facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
          ? Number(storyWorld.facingDirection)
          : null,
      };
    }

    return {
      ...STORY_VOID_CAVE_ENTRY_SPAWN,
      facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
        ? Number(storyWorld.facingDirection)
        : null,
    };
  }

  if (String(storyWorld?.building || "").trim().toLowerCase() === "gamehall") {
    if (isInsideBounds(requestedSpawn, STORY_GAME_HALL_BOUNDS)) {
      return {
        x: Number(requestedSpawn.x),
        y: Number(requestedSpawn.y),
        facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
          ? Number(storyWorld.facingDirection)
          : null,
      };
    }

    return {
      ...STORY_GAME_HALL_ENTRY_SPAWN,
      facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
        ? Number(storyWorld.facingDirection)
        : null,
    };
  }

  if (
    String(storyWorld?.building || "").trim().toLowerCase() === "questhall" &&
    storyWorld?.spawnId === STORY_QUEST_HALL_GLYDE_RETURN_SPAWN_ID
  ) {
    if (isInsideBounds(requestedSpawn, STORY_QUEST_HALL_BOUNDS)) {
      return {
        x: Number(requestedSpawn.x),
        y: Number(requestedSpawn.y),
        facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
          ? Number(storyWorld.facingDirection)
          : null,
      };
    }

    return {
      ...STORY_QUEST_HALL_ENTRY_SPAWN,
      facingDirection: isFiniteCoordinate(storyWorld?.facingDirection)
        ? Number(storyWorld.facingDirection)
        : null,
    };
  }

  return {
    x: isFiniteCoordinate(requestedSpawn.x) ? Number(requestedSpawn.x) : 730,
    y: isFiniteCoordinate(requestedSpawn.y) ? Number(requestedSpawn.y) : 560,
  };
};
